import '../../styles/beacon.css'
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LoadingProperties } from '../../../common/common.interfaces';
import { TableClient } from '@azure/data-tables';
import { DATA_SERVICES_TABLES_DEV, DATA_SERVICES_TABLES, CUSTOM_JSON_THEME, DATA_SERVICES_BLOBS, DATA_SERVICES_BLOBS_DEV } from '../../../common/common.constants';
import { InteractiveBrowserCredential } from '@azure/identity';
import { TENANT_ID, CLIENT_ID } from '../../../authConfig';
import { DateTime } from 'luxon';
import { BeaconFunctionAppsRow, BeaconFunctionsRow, BeaconProperties, BeaconTableRow, LogsTableRow } from '../../common/beacon.interfaces';
import BeaconFunctionAppTable from './table/BeaconFunctionAppTable';
import { Accordion, Badge, ButtonGroup, Col, Container, Form, Row, ToggleButton } from 'react-bootstrap';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { BlobServiceClient } from '@azure/storage-blob';
import JSONPretty from 'react-json-pretty';
import { blobToString } from '../../../blobs/blobs.service';
import BlobModalComponent from '../../../blobs/BlobModalComponent';
import FavoriteSvg from '../svg/FavoriteSvg';
import BeaconTabApps from './tabs/BeaconTabApps';
import BeaconEditFunctionAppModal from './modals/BeaconEditFunctionAppModal';
import BeaconEditFunctionModal from './modals/BeaconEditFunctionModal';
import { notify } from '../../../notify/notify';
import XSvg from '../svg/X-Svg';
import BadSvg from '../svg/BadSvg';
import ClockSvg from '../svg/ClockSvg';
import HamburgerSvg from '../svg/HamburgerSvg';
import ExclainSvg from '../svg/ExclaimSvg';
import ArrowRightSvg from '../svg/ArrowRightSvg';
import SearchSvg from '../svg/SearchSvg';
import { useLoading } from '../../../loading/LoadingContext';

export default function Beacon(props: BeaconProperties) {
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const [environment, setEnvironment]: [string, any] = useState('production')
  const [autoRefresh, setAutoRefresh]: [boolean, any] = useState(false)
  const [refresh, setRefresh]: [string, any] = useState(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  const [beaconRows, setBeaconRows]: [BeaconTableRow[], any] = useState(undefined)
  const [logsRows, setLogsRows]: [LogsTableRow[], any] = useState(undefined)
  const [blobName, setBlobName] = useState(undefined)
  const [blobContent, setBlobContent] = useState(undefined)
  const [showError, setShowError] = useState(undefined)
  const [lazyTimeInMinutes, setLazyTimeInMinutes] = useState(15)
  const [errorTimeInDays, setErrorTimeInDays] = useState(0.33) //within the last couple of hours hour
  const [longTimeInSeconds, setLongTimeInSeconds] = useState(60)
  const [firstLoad, setFirstLoad] = useState(true)
  const [favorites, setFavorites] = useState(undefined)
  const [filterSearch, setFilterSearch] = useState(undefined)
  const [filterFuncAppTags, setFilterFuncAppTags] = useState(undefined)
  const [filterFuncTags, setFilterFuncTags] = useState(undefined)
  const [showEditFunctionApp, setShowEditFunctionApp] = useState(undefined)
  const [showEditFunction, setShowEditFunction] = useState(undefined)
  const [editingFunctionApp, setEditingFunctionApp] = useState(undefined)
  const [editingFunction, setEditingFunction] = useState(undefined)

  const [beaconFunctionApps, setBeaconFunctionApps] = useState(undefined)
  const [beaconFunctions, setBeaconFunctions] = useState(undefined)

  const [funcAppUniqueTags, setFuncAppUniqueTags] = useState(undefined)
  const [funcUniqueTags, setFuncUniqueTags] = useState(undefined)

  const credential = new InteractiveBrowserCredential({
    tenantId: TENANT_ID,
    clientId: CLIENT_ID,
    authorityHost: 'https://login.microsoft.com'
  })

  /** Autorefresh interval */
  useEffect(() => {
    const intervalSetting = setInterval(
      () => {
        if(autoRefresh === true){
          setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
        }
      }, 30000
    )
    return () => {
      clearInterval(intervalSetting)
    }
  }, [autoRefresh])

  const getRowsByTableName = async function(tableName: string): Promise<any[]> {
    const tableClient = new TableClient(environment === 'development' ? DATA_SERVICES_TABLES_DEV : DATA_SERVICES_TABLES, tableName, credential)
    const tempRows = tableClient.listEntities()
    const rows = []
    for await(const row of tempRows){
      rows.push(row)
    }
    return rows
  }

  /** Load data from azure */
  useEffect(() => {
    const load = async function () {
      const showLoading = firstLoad === true || autoRefresh === false
      changeStep('Lighting the Beacon')
      toggleLoading(showLoading)

      try {
        changeSubStep('Loading Favorites')
        setFavorites(localStorage.getItem('beaconFavorites') ? JSON.stringify(localStorage.getItem('beaconFavorites')) : [])
  
        changeSubStep('Getting Beacon Records')
        setBeaconRows(await getRowsByTableName('beacon'))
  
        changeSubStep('Getting Log Records')
        setLogsRows(await getRowsByTableName('logs'))
  
        changeSubStep('Getting Function App Records')
        const funcApps: BeaconFunctionAppsRow[] = await getRowsByTableName('beaconFunctionApps')
  
        changeSubStep('Getting Function Records')
        const funcs: BeaconFunctionsRow[] = await getRowsByTableName('beaconFunctions')
  
        setBeaconFunctionApps(funcApps)
        setBeaconFunctions(funcs)
  
        changeSubStep('Determining Unique Tags')
        const uniqueFuncAppTags: Set<string> = new Set();
        for (const app of funcApps) {
          const tags: string[] = JSON.parse(app.tags ?? '[]');
          for (const tag of tags) {
            uniqueFuncAppTags.add(tag);
          }
        }
        setFuncAppUniqueTags(Array.from(uniqueFuncAppTags))
  
        const uniqueFuncTags: Set<string> = new Set();
        for (const func of funcs) {
          const tags: string[] = JSON.parse(func.tags ?? '[]');
          for (const tag of tags) {
            uniqueFuncTags.add(tag);
          }
        }
        
        setFuncUniqueTags(Array.from(uniqueFuncTags))
  
      }catch(error){
        console.log(error)
      }
      toggleLoading(false)
    }
    load()
  }, [environment, refresh, autoRefresh])

  const handleDownloadError = useCallback((e) => {
    const load = async function () {
      changeStep('Turning the Beacon')
      changeSubStep('Preparing Download Request')
      toggleLoading(true)
      try {
        const {app, func, blobpath} = e.currentTarget.dataset
        const blobServiceClient = new BlobServiceClient(environment === 'development' ? DATA_SERVICES_BLOBS_DEV : DATA_SERVICES_BLOBS, credential)
        const containerService = blobServiceClient.getContainerClient('storage')
        const blobClient = containerService.getBlobClient(blobpath)
        changeSubStep('Downloading Error')
        const data = await blobClient.download()
        const downloaded = await blobToString(await data.blobBody)
        changeSubStep('Preparing for User Download')
        const blob = new Blob([downloaded], { type: 'json' })
        const url = window.URL.createObjectURL(blob)
        const justBlobName = blobpath.split('/').pop()
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${app}___${func}___${justBlobName}`);
        document.body.appendChild(link);
        changeSubStep('Executing Download to Client')
        link.click();
        document.body.removeChild(link);
      }catch(error){
        console.error(error)
        notify('error', `Failed to download. ${error}`)
      }

      toggleLoading(false)
    }
    load()
  }, [environment])

  const handleViewError = useCallback((e) => {
    const load = async function () {
      changeStep('Turning the Beacon')
      changeSubStep('Preparing Download Request')
      toggleLoading(true)

      try {
        const {app, func, blobpath} = e.currentTarget.dataset
        const blobServiceClient = new BlobServiceClient(environment === 'development' ? DATA_SERVICES_BLOBS_DEV : DATA_SERVICES_BLOBS, credential)
        const containerService = blobServiceClient.getContainerClient('storage')
        const blobClient = containerService.getBlobClient(blobpath)
        changeSubStep('Downloading Error')
        const data = await blobClient.download()
        const downloaded = await blobToString(await data.blobBody)
        changeSubStep('Preparing Error Modal')
        setBlobName(`${app} - ${func} - ${blobpath.split('/').pop().replace('.json', '')}`)
        setBlobContent(downloaded)
        toggleLoading(false)
        setShowError(true)
      }catch(error){
        console.error(error)
        toggleLoading(false)
        notify('error', `Failed to view error content. ${error}`)
      }
    }
    load()
  }, [environment])

  const handleFavoriteClicked = useCallback((e) => {
    const { isfavorited, functionapp, func } = e.currentTarget.dataset;
    // Check if favorites are already available in localStorage
    let favorites = JSON.parse(localStorage.getItem('beaconFavorites')) || [];
  
    let faveSet;
  
    if (isfavorited === 'true') { // `isfavorited` should be a string, not boolean
      faveSet = new Set([...favorites, `${functionapp}___${func}`]); // Add to favorites
    } else {
      faveSet = new Set(favorites.filter(f => f !== `${functionapp}___${func}` )); // Remove from favorites
    }
  
    // Save the updated favorite set as an array in localStorage
    localStorage.setItem('beaconFavorites', JSON.stringify([...faveSet]));
    setFavorites([...faveSet])
  }, []);

  const handleFilterFunctionApp = useCallback((e) => {
    setFilterSearch(e.target.value)
  }, [])

  const handleEditFunctionApp = useCallback((e) => {
    const {functionapp} = e.currentTarget.dataset
    let beaconFunctionAppRow = beaconFunctionApps?.find(bfa => bfa.partitionKey === functionapp)
    if(!beaconFunctionAppRow){
      beaconFunctionAppRow = {
        partitionKey: functionapp,
        rowKey: functionapp
      }
    }
    setEditingFunctionApp(beaconFunctionAppRow)
    setShowEditFunctionApp(true)
  }, [beaconFunctionApps])

  const handleEditFunction = useCallback((e) => {
    const {func, functionapp} = e.currentTarget.dataset
    let beaconFunctionRow = beaconFunctions?.find(bf => bf.partitionKey === functionapp && bf.rowKey === func)
    if(!beaconFunctionRow){
      beaconFunctionRow = {
        partitionKey: functionapp,
        rowKey: func
      }
    }
    setEditingFunction(beaconFunctionRow)
    setShowEditFunction(true)
  }, [beaconFunctions])

  const handleFuncAppTagClicked = useCallback((e) => {
    const tag = e.currentTarget.dataset.tag
    const existingTags: string[] = filterFuncAppTags ?? []
    const alreadySelected = existingTags?.includes(tag)
    let newTags = []
    if(alreadySelected === true){
      //remove it
      newTags = existingTags.filter(t => t !== tag)
    } else {
      newTags = [
        ...existingTags,
        tag
      ]
    }
    setFilterFuncAppTags(newTags);
  }, [filterFuncAppTags])

  const handleFuncTagClicked = useCallback((e) => {
    const tag = e.currentTarget.dataset.tag
    const existingTags: string[] = filterFuncTags ?? []
    const alreadySelected = existingTags?.includes(tag)
    let newTags = []
    if(alreadySelected === true){
      //remove it
      newTags = existingTags.filter(t => t !== tag)
    } else {
      newTags = [
        ...existingTags,
        tag
      ]
    }
    setFilterFuncTags(newTags);
  }, [filterFuncTags]);

  const beaconView = useMemo(() => {
    if(!beaconRows) return

    const showLoading = firstLoad === true || autoRefresh === false
    changeStep('Preparing Beacon Views')
    changeSubStep('Initializing type tables')
    toggleLoading(showLoading)

    const errorFunctions: BeaconTableRow[] = []
    const longFunctions: BeaconTableRow[] = []
    const lastFunctions: BeaconTableRow[] = []
    const favoriteFunctions: BeaconTableRow[] = []
    const dirtyFunctions: BeaconTableRow[] = []

    changeSubStep('Filtering by Search Text')
    const allMap: Map<string, BeaconTableRow[]> = new Map()
    let filteredBeacons = beaconRows
    if(typeof filterSearch === 'string' && filterSearch.trim().length > 0){
      filteredBeacons = filteredBeacons.filter(b => 
        b.partitionKey?.toLowerCase().includes(filterSearch.toLowerCase()) || 
        b.rowKey?.toLowerCase().includes(filterSearch.toLowerCase())
      );
    }

    changeSubStep('Filtering by Function Tags')
    if(filterFuncTags && filterFuncTags.length > 0){
      filteredBeacons = filteredBeacons.filter(b => {
        const funcRow: BeaconFunctionsRow = beaconFunctions?.find(bf => bf.partitionKey === b.partitionKey && bf.rowKey === b.rowKey)
        const funcTags: string[] = JSON.parse(funcRow?.tags ?? '[]')
        return funcTags.some(tag => filterFuncTags.includes(tag))
      })
    }

    changeSubStep('Filtering by Function App Tags')
    if(filterFuncAppTags && filterFuncAppTags.length > 0){
      filteredBeacons = filteredBeacons.filter(b => {
        const appRow: BeaconFunctionAppsRow = beaconFunctionApps?.find(bfa => bfa.partitionKey === b.partitionKey)
        const funcAppTags: string[] = JSON.parse(appRow?.tags ?? '[]')
        return funcAppTags.some(tag => filterFuncAppTags.includes(tag))
      })
    }

    changeSubStep('Manipulating Time & Space')
    for(const beacon of filteredBeacons ?? []){
      if(!allMap.has(beacon.partitionKey)){
        allMap.set(beacon.partitionKey, [])
      }
      allMap.get(beacon.partitionKey).push(beacon)

      const lastCompleteSeconds = DateTime.utc().diff(DateTime.fromISO(beacon.lastComplete), 'seconds').seconds
      const lastCompleteMinutes = DateTime.utc().diff(DateTime.fromISO(beacon.lastComplete), 'minutes').minutes
      const lastCompleteHours = DateTime.utc().diff(DateTime.fromISO(beacon.lastComplete), 'hours').hours
      const lastCompleteDays = DateTime.utc().diff(DateTime.fromISO(beacon.lastComplete), 'days').days
      beacon.sinceLastCompleteSeconds = lastCompleteSeconds
      beacon.sinceLastCompleteMinutes = lastCompleteMinutes
      beacon.sinceLastCompleteHours = lastCompleteHours
      beacon.sinceLastCompleteDays = lastCompleteDays

      const lastRunSeconds = DateTime.fromISO(beacon.lastComplete).diff(DateTime.fromISO(beacon.lastRun), 'seconds').seconds
      const lastRunMinutes = DateTime.fromISO(beacon.lastComplete).diff(DateTime.fromISO(beacon.lastRun), 'minutes').minutes
      const lastRunHours = DateTime.fromISO(beacon.lastComplete).diff(DateTime.fromISO(beacon.lastRun), 'hours').hours
      const lastRunDays = DateTime.fromISO(beacon.lastComplete).diff(DateTime.fromISO(beacon.lastRun), 'days').days
      beacon.startToCompleteSeconds = lastRunSeconds
      beacon.startToCompleteMinutes = lastRunMinutes
      beacon.startToCompleteHours = lastRunHours
      beacon.startToCompleteDays = lastRunDays

      beacon.isFavorite = favorites.includes(`${beacon.partitionKey}___${beacon.rowKey}`)

      const logsRow = logsRows?.find(l => l.partitionKey === beacon.partitionKey && l.rowKey === beacon.rowKey)
      if(logsRow){
        if(DateTime.utc().diff(DateTime.fromISO(logsRow.timestamp), 'days').days < errorTimeInDays) {
          errorFunctions.push(beacon)
          beacon.logsTableRow = logsRow
        } 
      }

      if(lastRunSeconds > longTimeInSeconds){
        longFunctions.push(beacon)
      }
      if(lastCompleteMinutes > lazyTimeInMinutes){
        lastFunctions.push(beacon)
      }
      if(beacon.isFavorite === true){
        favoriteFunctions.push(beacon)
      }

      const funcRow: BeaconFunctionsRow = beaconFunctions?.find(bf => bf.partitionKey === beacon.partitionKey && bf.rowKey === beacon.rowKey)
      beacon.functionsRow = funcRow

      const funcAppRow: BeaconFunctionAppsRow = beaconFunctionApps?.find(bfa => bfa.partitionKey === beacon.partitionKey)
      if(!funcRow || !funcAppRow){
        dirtyFunctions.push(beacon)
      }
    }

    changeSubStep('Preparing Favorites')
    const favoriteFunctionAppMap: Map<string, BeaconTableRow[]> = new Map()
    for(const beacon of favoriteFunctions){
      if(!favoriteFunctionAppMap.has(beacon.partitionKey)){
        favoriteFunctionAppMap.set(beacon.partitionKey, [])
      }
      favoriteFunctionAppMap.get(beacon.partitionKey).push(beacon)
    }

    const favoriteComponents = []
    const favoriteTocItems = []
    for(const functionApp of favoriteFunctionAppMap.keys()){
      const functionAppsRow = beaconFunctionApps?.find(bfa => bfa.partitionKey === functionApp)
      favoriteComponents.push(
        <BeaconFunctionAppTable 
          key={`${functionApp}-favorites-table`}
          beacons={favoriteFunctionAppMap.get(functionApp)}
          functionApp={functionApp}
          beaconFunctionAppsRow={functionAppsRow}
          handleDownloadError={handleDownloadError}
          handleFavoriteClicked={handleFavoriteClicked}
          handleViewError={handleViewError}
          handleEditFunction={handleEditFunction}
          handleEditFunctionApp={handleEditFunctionApp}
          type='fave'
        />
      )
      favoriteTocItems.push(<a href={`#${functionApp}`}>{functionApp}</a>)
    }

    changeSubStep('Preparing Long Running Functions')
    const longRunnerFunctionAppMap: Map<string, BeaconTableRow[]> = new Map()
    const sortedLongRunners = longFunctions.sort((a, b) => b.startToCompleteSeconds - a.startToCompleteSeconds)
    for(const beacon of sortedLongRunners ?? []){
      if(!longRunnerFunctionAppMap.has(beacon.partitionKey)){
        longRunnerFunctionAppMap.set(beacon.partitionKey, [])
      }
      longRunnerFunctionAppMap.get(beacon.partitionKey).push(beacon)
    }

    const longComponents = []
    const longTocItems = []
    for(const functionApp of longRunnerFunctionAppMap.keys()){
      const functionAppsRow = beaconFunctionApps?.find(bfa => bfa.partitionKey === functionApp)
      longComponents.push(
        <BeaconFunctionAppTable 
          key={`${functionApp}-long-table`}
          beacons={longRunnerFunctionAppMap.get(functionApp)} 
          functionApp={functionApp} 
          beaconFunctionAppsRow={functionAppsRow}
          handleDownloadError={handleDownloadError} 
          handleViewError={handleViewError} 
          handleFavoriteClicked={handleFavoriteClicked}
          handleEditFunction={handleEditFunction}
          handleEditFunctionApp={handleEditFunctionApp}
          type='long' 
          />
      )
      longTocItems.push(<a href={`#${functionApp}`}>{functionApp}</a>)
    }

    changeSubStep('Preparing Erroring Functions')
    //Group up the errors. no recent logs means no errors! :)
    const errorMap: Map<string, BeaconTableRow[]> = new Map()
    const sortedErrorFunctions = errorFunctions.sort((a, b) => {return a.sinceLastCompleteSeconds - b.sinceLastCompleteSeconds})
    for(const beacon of sortedErrorFunctions ?? []){
      if(!errorMap.has(beacon.partitionKey)){
        errorMap.set(beacon.partitionKey, [])
      }
      if(beacon.logsTableRow){
        errorMap.get(beacon.partitionKey).push(beacon)
      }
    }

    const errorComponents = []
    const errorFuncApps = []
    for(const functionApp of errorMap.keys()){
      const beacons = errorMap.get(functionApp)
      const functionAppsRow = beaconFunctionApps?.find(bfa => bfa.partitionKey === functionApp)
      errorComponents.push(
        <BeaconFunctionAppTable 
          key={`${functionApp}-error-table`}
          beacons={beacons.filter(b => typeof b.logsTableRow !== 'undefined')} 
          beaconFunctionAppsRow={functionAppsRow}
          functionApp={functionApp} 
          handleDownloadError={handleDownloadError} 
          handleViewError={handleViewError} 
          handleFavoriteClicked={handleFavoriteClicked}
          handleEditFunction={handleEditFunction}
          handleEditFunctionApp={handleEditFunctionApp}
          type='error' 
          />
      )
      errorFuncApps.push(functionApp)
    }

    changeSubStep('Preparing Lazy Functions')
    const lazyMap: Map<string, BeaconTableRow[]> = new Map()
    const sortedLazy = lastFunctions.sort((a, b) => b.sinceLastCompleteSeconds - a.sinceLastCompleteSeconds);
    for(const lazy of sortedLazy){
      if(!lazyMap.has(lazy.partitionKey)){
        lazyMap.set(lazy.partitionKey, [])
      }
      lazyMap.get(lazy.partitionKey).push(lazy)
    }

    const lazyComponents = []
    const lazyFuncApps = []
    for(const functionApp of lazyMap.keys()){
      const functionAppsRow = beaconFunctionApps?.find(bfa => bfa.partitionKey === functionApp)
      lazyComponents.push(
        <BeaconFunctionAppTable 
          key={`${functionApp}-lazy-table`}
          beacons={lazyMap.get(functionApp)} 
          functionApp={functionApp} 
          beaconFunctionAppsRow={functionAppsRow}
          handleDownloadError={handleDownloadError} 
          handleViewError={handleViewError} 
          handleFavoriteClicked={handleFavoriteClicked}
          handleEditFunction={handleEditFunction}
          handleEditFunctionApp={handleEditFunctionApp}
          type='lazy'
          />
      )
      lazyFuncApps.push(functionApp)
    }

    changeSubStep('Preparing All Functions')
    const allComponents = []
    const allFuncApps = []
    for(const functionApp of allMap.keys()){
      const functionAppsRow = beaconFunctionApps?.find(bfa => bfa.partitionKey === functionApp)
      allComponents.push(
        <BeaconFunctionAppTable 
          key={`${functionApp}-all-table`}
          beacons={allMap.get(functionApp)} 
          functionApp={functionApp} 
          beaconFunctionAppsRow={functionAppsRow}
          handleDownloadError={handleDownloadError} 
          handleViewError={handleViewError} 
          handleFavoriteClicked={handleFavoriteClicked}
          handleEditFunction={handleEditFunction}
          handleEditFunctionApp={handleEditFunctionApp}
          type='all'
          />
      )
      allFuncApps.push(functionApp)
    }

    changeSubStep('Preparing Functions that could use settings')
    const dirtyComponents = []
    const dirtyFuncApps = []
    for(const functionApp of allMap.keys()){
      const functionAppsRow = beaconFunctionApps?.find(bfa => bfa.partitionKey === functionApp)
      const beacons = allMap.get(functionApp).filter(f => typeof f.functionsRow === 'undefined')
      if(beacons.length > 0){
        dirtyComponents.push(
          <BeaconFunctionAppTable 
            key={`${functionApp}-dirty-table`}
            beacons={beacons} 
            functionApp={functionApp} 
            beaconFunctionAppsRow={functionAppsRow}
            handleDownloadError={handleDownloadError} 
            handleViewError={handleViewError} 
            handleFavoriteClicked={handleFavoriteClicked}
            handleEditFunction={handleEditFunction}
            handleEditFunctionApp={handleEditFunctionApp}
            type='dirty'
            />
        )
        dirtyFuncApps.push(functionApp)
      } else if(beacons.length === 0 && !functionAppsRow){
        dirtyComponents.push(
          <BeaconFunctionAppTable 
            key={`${functionApp}-dirty-table`}
            beacons={[]} 
            functionApp={functionApp} 
            beaconFunctionAppsRow={functionAppsRow}
            handleDownloadError={handleDownloadError} 
            handleViewError={handleViewError} 
            handleFavoriteClicked={handleFavoriteClicked}
            handleEditFunction={handleEditFunction}
            handleEditFunctionApp={handleEditFunctionApp}
            type='dirty'
            />
        )
        dirtyFuncApps.push(functionApp)
      }
    }

    toggleLoading(false)
    setFirstLoad(false)

    return <div>
      <div id='filters'>
        <Accordion defaultActiveKey="0" className='custom-accordion'>
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <span className='beacon-header'>
                <strong>FILTERS</strong> {
                  filterSearch ? <><ArrowRightSvg height={25}/> FIND "{filterSearch}"</> : ''} 
                  {filterFuncAppTags?.map(tag => {return <span key={`${tag}-func-app`}> <ArrowRightSvg height={25} /> {tag}</span>})}
                  {filterFuncTags?.map(tag => {return <span key={`${tag}-func`}><ArrowRightSvg height={25} /> {tag}</span>})
                }
              </span>
            </Accordion.Header>
            <Accordion.Body>
              <span className='beacon-label' title='Clear all filters' onClick={() => {setFilterFuncTags([]); setFilterFuncAppTags([]); setFilterSearch(undefined)}} style={{cursor: 'pointer'}}>
                <XSvg height={15}/>CLEAR ALL
              </span><span className='beacon-label'> - {filterFuncAppTags?.length ?? 0} function app and {filterFuncTags?.length ?? 0} function tag(s) searching for {filterSearch ? `"${filterSearch}"` : 'nothing'}.</span>
              <br/>
              <div>
                <Container fluid className="p-0 text-start">
                  <Row>
                    <Col>
                      <br/>
                      <span className='beacon-header'>FUNCTION APP TAGS</span>
                      <br/>
                      {!funcAppUniqueTags || funcAppUniqueTags?.length === 0 ? <>
                        <br/>
                        <span key='no-func-app-tags' className='beacon-label'>No function app tags created yet.</span>
                      </> : ''}
                      {funcAppUniqueTags?.length > 0 && <div>
                        <span className='beacon-label' title='Clear function app tags from the filter' onClick={() => {setFilterFuncAppTags([])}} style={{cursor: 'pointer'}}>
                          <XSvg height={15}/> CLEAR APP TAGS
                        </span>
                        <br/>
                        {funcAppUniqueTags?.sort()?.map(tag => (
                          <>
                          <Badge 
                            key={`${tag}-func-app-badge`} 
                            data-tag={tag} 
                            onClick={handleFuncAppTagClicked} 
                            style={{cursor: 'pointer'}} 
                            bg={filterFuncAppTags?.includes(tag) ? 'success' : 'secondary'}>{tag}</Badge>
                          &nbsp;
                          </>
                        ))}
                      
                      </div>}
                      <br/>
                      <span className='beacon-header'>FUNCTION TAGS</span><br/>
                      <span className='beacon-label' title='Clear function tags from the filter' onClick={() => {setFilterFuncTags([])}} style={{cursor: 'pointer'}}>
                        <XSvg height={15}/> CLEAR FUNCTION TAGS
                      </span>
                      {!funcUniqueTags || funcUniqueTags?.length === 0 ? <>
                        <br/>
                        <span key='no-func-tags' className='beacon-label'>No function tags created yet.</span>
                      </> : ''}
                      {funcUniqueTags?.length > 0 && <>
                        <br/>
                      </>}
                      {funcUniqueTags?.sort()?.map(tag => (
                        <>
                        <Badge 
                          key={`${tag}-func-badge`} 
                          data-tag={tag} 
                          onClick={handleFuncTagClicked} 
                          style={{cursor: 'pointer'}} 
                          bg={filterFuncTags?.includes(tag) ? 'success' : 'secondary'}>{tag}</Badge>
                        &nbsp;
                        </>
                      ))}
                    </Col>
                    <Col>
                      <div style={{width: '200px'}}>
                        <span title='Clear search content from the filter' onClick={() => {setFilterSearch(undefined)}} style={{cursor: 'pointer', float: 'right'}}>
                          <XSvg height={15}/>
                        </span>
                        <span className='beacon-label'>
                          <SearchSvg height={15} />
                          SEARCH
                        </span>
                        <Form.Control 
                          value={filterSearch ?? ''} 
                          type="input" 
                          onChange={handleFilterFunctionApp} 
                          placeholder="Enter a function or function app name..."
                        />
                      </div>
                    </Col>
                  </Row>
                </Container>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
      <br/>
      <div>
        <Tabs>
          <TabList>
            <Tab key='errorsTab'><span className='beacon-header'><BadSvg height={25}/> ERRORS</span></Tab>
            {favoriteFunctions.length > 0 ? 
              <Tab key='favesTab'><span className='beacon-header'><FavoriteSvg height={25} selected={true} /> FAVORITES</span></Tab>
            : ''}
            <Tab key='longTab'><span className='beacon-header'><ClockSvg height={25}/> LONG</span></Tab>
            <Tab key='oldTab'><span className='beacon-header'><HamburgerSvg height={25}/> LAZY</span></Tab>
            <Tab key='dirtyTab'><span className='beacon-header'><ExclainSvg height={25}/> ATTENTION</span></Tab>
            <Tab key='allTab'><span className='beacon-header'>EVERYTHING</span></Tab>
          </TabList>

          <TabPanel key='error'>
            <div>
              <span className='beacon-header'>ERROR FILTERS</span>
              <br/>
              <span className='beacon-label'>View function errors within {errorTimeInDays} days(s) | {(errorTimeInDays*24).toFixed(2)} hour(s) | {((errorTimeInDays*24)*60).toFixed(2)} minute(s).</span>
              <span title='Change the timespan'>
                <Form.Range max={4} min={0.01} step={0.01} defaultValue={errorTimeInDays} onChange={(e) => {setErrorTimeInDays(+e.currentTarget.value)}}></Form.Range>
              </span>
              <BeaconTabApps
                key={`errorTabApps`}
                components={errorComponents}
                funcs={errorFunctions}
                funcApps={errorFuncApps}
              />
            </div>            
          </TabPanel>

          {favoriteFunctions.length > 0 ? 
            <TabPanel key='faves'>
              <span className='beacon-header'>FAVORITES FILTERS</span>
              <br/>
              <span className='beacon-label'>View your favorited functions.</span>
              <BeaconTabApps
                key={`favoritesTabApps`}
                components={favoriteComponents}
                funcs={favoriteFunctions}
                funcApps={favoriteTocItems}
              />
            </TabPanel>
          : ''}

          <TabPanel key='long'>
            <span className='beacon-header'>LONG RUNNING FUNCTIONS FILTER</span>
            <br/>
            <span className='beacon-label'>View functions running longer than {longTimeInSeconds.toFixed(0)} second(s) | {(longTimeInSeconds/60).toFixed(2)} minutes(s) | {((longTimeInSeconds/60)/60).toFixed(2)} hour(s).</span>
            <span title='Change the timespan'>
              <Form.Range max={10080} min={1} step={1} defaultValue={longTimeInSeconds} onChange={(e) => {setLongTimeInSeconds(+e.currentTarget.value)}}></Form.Range>
            </span>
            <BeaconTabApps
              key={`longTabApps`}
              components={longComponents}
              funcs={longFunctions}
              funcApps={longTocItems}
            />
          </TabPanel>

          <TabPanel key='lazy'>
            <span className='beacon-header'>LAZY FUNCTIONS FILTER</span>
            <br/>
            <span className='beacon-label'>View functions that have not completed within the last {lazyTimeInMinutes.toFixed(0)} minutes(s) | {(lazyTimeInMinutes/60).toFixed(2)} hour(s). Does not include HTTP type functions if set.</span>
            <span title='Change the timespan'>
              <Form.Range max={1440} min={1} step={1} defaultValue={lazyTimeInMinutes} onChange={(e) => {setLazyTimeInMinutes(+e.currentTarget.value)}}></Form.Range>
            </span>
            <BeaconTabApps
              key={`lazyTabApps`}
              components={lazyComponents}
              funcs={lastFunctions}
              funcApps={lazyFuncApps}
            />
          </TabPanel>

          <TabPanel key='dirty'>
            <span className='beacon-header'>ATTENTION FUNCTIONS FILTER</span>
            <br/>
            <span className='beacon-label'>View all functions and apps that require tags or function settings.</span>
            <BeaconTabApps
              key={`dirtyTabApps`}
              components={dirtyComponents}
              funcs={dirtyFunctions}
              funcApps={dirtyFuncApps}
            />
          </TabPanel>

          <TabPanel key='all'>
            <span className='beacon-header'>ALL FUNCTIONS FILTER</span>
            <br/>
            <span className='beacon-label'>View all functions and apps that require tags or function settings.</span>
            <BeaconTabApps
              key={`allTabApps`}
              components={allComponents}
              funcs={filteredBeacons}
              funcApps={allFuncApps}
            />
          </TabPanel>

        </Tabs>
      </div>
    </div>
  }, [
    beaconRows, 
    logsRows, 
    errorTimeInDays, 
    lazyTimeInMinutes, 
    longTimeInSeconds, 
    firstLoad, 
    favorites, 
    filterSearch, 
    beaconFunctionApps, 
    beaconFunctions, 
    filterFuncAppTags,
    filterFuncTags,
    refresh,
    autoRefresh
  ])

  const handleEditFunctionAppClose = useCallback((e) => {
    setEditingFunctionApp(undefined)
    setShowEditFunctionApp(false)
  }, [])

  const handleEditFunctionClose = useCallback((e) => {
    setEditingFunction(undefined)
    setShowEditFunction(false)
  }, [])

  const handleEditFunctionAppSave = useCallback((funcAppRow: BeaconFunctionAppsRow) => {
    const saveFunc = async function (funcAppRow: BeaconFunctionAppsRow) {
      try {
        changeStep('Saving Scrolls')
        changeSubStep('Saving Beacon Function App Content')
        toggleLoading(true)

        const tableClient = new TableClient(DATA_SERVICES_TABLES, 'beaconFunctionApps', credential)
        await tableClient.upsertEntity(funcAppRow as any, 'Replace')
        notify('success', `Successfully saved ${funcAppRow.partitionKey} function app!`)
        setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
      }catch(error){
        notify('error', `Failed to update ${funcAppRow.partitionKey} function app! ${error}`)
      }
      setEditingFunctionApp(undefined)
      setShowEditFunctionApp(false)
      toggleLoading(false)
    }
    saveFunc(funcAppRow)
  }, [])

  const handleEditFunctionSave = useCallback((funcRow: BeaconFunctionsRow) => {
    const saveFunc = async function (funcRow: BeaconFunctionsRow) {
      try {
        changeStep('Saving Scrolls')
        changeSubStep('Saving Beacon Function Content')
        toggleLoading(true)
        const tableClient = new TableClient(DATA_SERVICES_TABLES, 'beaconFunctions', credential)
        await tableClient.upsertEntity(funcRow as any, 'Replace')
        notify('success', `Successfully saved ${funcRow.partitionKey} ${funcRow.rowKey} function!`)
        setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
      }catch(error){
        notify('error', `Failed to update ${funcRow.partitionKey} ${funcRow.rowKey} function! ${error}`)
      }
      setEditingFunction(undefined)
      setShowEditFunction(false)
      toggleLoading(false)
    }
    saveFunc(funcRow)
  }, [])

  const viewComponents = useMemo(() => {
    return <div className='beacon-main'>
      <BeaconEditFunctionAppModal tags={funcAppUniqueTags} saveCallback={handleEditFunctionAppSave} closeCallback={handleEditFunctionAppClose} visible={showEditFunctionApp} functionAppRow={editingFunctionApp} functionApp={editingFunctionApp?.partitionKey}/>
      <BeaconEditFunctionModal
        tags={funcUniqueTags}
        saveCallback={(e) => handleEditFunctionSave(e)} // Ensure this matches () => void
        closeCallback={() => handleEditFunctionClose(undefined)} // Ensure this matches () => void
        visible={showEditFunction}
        func={editingFunction}
        functionApp={editingFunction?.partitionKey}
        functionName={editingFunction?.rowKey}
      />

      <BlobModalComponent raw={blobContent} blobData={<JSONPretty data={blobContent} theme={CUSTOM_JSON_THEME} style={{fontSize: '1.5em'}} />} blobName={blobName} closeModal={() => {setShowError(false)}} modalVisible={showError === true}/>
      <div id="top"></div>
      <div className='beacon-card'>
        <table className='beacon-table'>
          <tbody>
            <tr>
              <td><span className='beacon-header'>BEACON</span></td>
            </tr>
            <tr>
              <td>
                <span className='beacon-label'>as of {refresh}</span>
              </td>
            </tr>
            <tr>
              <td>
                <div>
                  <span className='beacon-label'>auto-refresh</span>
                  <br/>
                  <ButtonGroup>
                    <ToggleButton
                      type="radio"
                      variant={'secondary'}
                      value="on"
                      checked={autoRefresh}
                      onChange={() => setAutoRefresh(true)}
                      id="refreshOn"
                      className={`px-4 ${autoRefresh ? 'active' : ''}`}
                    >
                      ON
                    </ToggleButton>
                    <ToggleButton
                      type="radio"
                      variant={'secondary'}
                      value="off"
                      checked={!autoRefresh}
                      onChange={() => setAutoRefresh(false)}
                      id="refreshOff"
                      className={`px-4 ${!autoRefresh ? 'active' : ''}`}
                    >
                      OFF
                    </ToggleButton>
                  </ButtonGroup>
                  &nbsp;&nbsp;&nbsp;
                  <span title='Refresh everything' style={{cursor: 'pointer'}} onClick={() => {setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))}}>
                    <svg
                      height='15'
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-6 h-6"
                    >
                      <path d="M21 2v6h-6" />
                      <path d="M3 12a9 9 0 0 1 15-6.7l3 3" />
                      <path d="M3 22v-6h6" />
                      <path d="M21 12a9 9 0 0 1-15 6.7l-3-3" />
                    </svg>
                  </span>
                </div>  
              </td>
              <td>
                <div style={{textAlign: 'center'}}>
                  <span className='beacon-label'>environment</span>
                  <br/>
                  <ButtonGroup>
                    <ToggleButton
                      type="radio"
                      variant={'secondary'}
                      value="development"
                      checked={environment === 'development'}
                      onChange={() => setEnvironment('development')}
                      id="developmentRadio"
                      className={`px-4 ${environment === 'development' ? 'active' : ''}`}
                    >
                      TEST
                    </ToggleButton>
                    <ToggleButton
                      type="radio"
                      variant={'secondary'}
                      value="production"
                      checked={environment === 'production'}
                      onChange={() => setEnvironment('production')}
                      id="productionRadio"
                      className={`px-4 ${environment === 'production' ? 'active' : ''}`}
                    >
                      PROD
                    </ToggleButton>
                  </ButtonGroup>
                </div> 
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {beaconView}
  </div>
  }, [
    blobContent, 
    blobName, 
    showError, 
    autoRefresh,
    showEditFunction,
    editingFunction,
    showEditFunctionApp,
    editingFunctionApp,
    refresh,
    funcAppUniqueTags,
    funcUniqueTags,
    environment
  ])

  return viewComponents
}
